import React, { useEffect } from 'react';
import ErrorMessage from '../../../../components/text/ErrorMessage';
import {
  AppPermissions,
  DateFormats,
  Holiday,
  formatDate,
  useHasPermission,
} from '../../../../core';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import { IDateRange } from '../../../../core/components/list-filters/DateRangeListFilter';
import classnames from 'classnames';
import moment from 'moment';
import { Controller, useForm } from 'react-hook-form';
const CreateForm: React.FunctionComponent<{
  onSubmit: (data: Holiday) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  data?: Holiday;
  error?: string;
  onDelete: (id?: string) => void;
  resetError?: (id?: string) => void;
}> = (props) => {
  const canDeleteHoliday = useHasPermission(AppPermissions.HolidaySettings);
  const canCreateHoliday = useHasPermission(AppPermissions.HolidaySettings);
  const canUpdateHoliday = useHasPermission(AppPermissions.HolidaySettings);
  const [isDeleteModalOpen, setDeleteModalOpen] = React.useState<boolean>(
    false
  );

  const { control, errors, watch } = useForm<{
    holidayTitle: string;
  }>({
    reValidateMode: 'onChange',
    mode: 'onSubmit',
  });

  const [dateFilters, setDateFilters] = React.useState<IDateRange>(
    props.data?.fromDate && props.data?.toDate
      ? {
          startDate: new Date(props.data?.fromDate),
          endDate: new Date(props.data?.toDate),
        }
      : {}
  );
  const onDelete = () => {
    setDeleteModalOpen(true);
  };
  const onCancelDelete = () => {
    setDeleteModalOpen(false);
  };

  useEffect(() => {
    if (props.resetError) {
      props.resetError();
    }
  }, [watch('holidayTitle'), dateFilters.startDate, dateFilters.endDate]);
  const renderHolidayTitle = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">Holiday Title</label>
          <Controller
            rules={{
              validate: {
                required: (value) => !!value || 'Title is required',
              },
            }}
            control={control}
            name="holidayTitle"
            defaultValue={props.data?.holidayTitle}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Holiday Title"
                name={name}
                value={value}
                onChange={onChange}
                id="holidayTitle"
              />
            )}
          />
          <ErrorMessage>{errors.holidayTitle}</ErrorMessage>
        </div>
      </div>
    );
  };
  const renderDateRangeSelector = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase"> Holiday Period </label>
          <div className="col-md-auto ml-auto border col-12 mt-md-0 mt-3 d-flex align-items-center">
            <DateRangePicker
              onApply={(e, picker: any) => {
                setDateFilters({
                  startDate: picker.startDate?.toDate(),
                  endDate: picker.endDate?.toDate(),
                });
              }}
              initialSettings={{
                startDate: dateFilters.startDate,
                endDate: dateFilters.endDate,
                locale: {
                  format: DateFormats.NormalDateFormatWithoutHiphons,
                },
                autoUpdateInput: false,
              }}
            >
              <input
                type="text"
                className={classnames({
                  'form-control border-0 px-0 form-control-sm font-sm': true,
                  'font-weight-semibold': !!(
                    dateFilters?.startDate || dateFilters?.endDate
                  ),
                  'font-weight-normal text-muted': !(
                    dateFilters?.startDate || dateFilters?.endDate
                  ),
                })}
                value={`${
                  dateFilters?.startDate || dateFilters?.endDate
                    ? `${moment(dateFilters.startDate).format(
                        DateFormats.NormalDateFormatWithoutHiphons
                      )} - ${moment(dateFilters.endDate).format(
                        DateFormats.NormalDateFormatWithoutHiphons
                      )}`
                    : ' Choose Date Range '
                }`}
              />
            </DateRangePicker>
            <i className="icon-calendar ml-1"></i>
          </div>
        </div>
      </div>
    );
  };

  const renderDeleteConfirm = () => {
    return isDeleteModalOpen ? (
      <>
        <div
          className="modal fade show in"
          id="exampleModal"
          aria-labelledby="exampleModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-confirm modal-dialog-centered">
            <div className="modal-content rounded-0">
              <div className="modal-body text-center">
                <div className="container">
                  <div className="row">
                    <div className="col">
                      <p>
                        You are about to delete the Holiday from -
                        {formatDate(props?.data?.fromDate!, 'DD MMM YYYY')} to{' '}
                        {formatDate(props?.data?.toDate!, 'DD MMM YYYY')}
                      </p>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-auto mr-auto">
                      <button
                        type="button"
                        className="btn btn-outline-dark py-2 font-weight-semibold font-normal px-4 mr-1"
                        onClick={onCancelDelete}
                      >
                        Cancel
                      </button>
                    </div>
                    <div className="col-auto">
                      <button
                        type="submit"
                        className="btn btn-primary py-2 font-weight-semibold font-normal px-4 "
                        onClick={() => props.onDelete(props.data?._id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show"></div>
      </>
    ) : (
      <></>
    );
  };

  const renderButton = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3 text-right pt-3">
          {props.data?._id && canDeleteHoliday && (
            <button
              type="button"
              className="btn btn-outline-gray py-2 font-weight-semibold font-normal px-4 mr-1"
              onClick={onDelete}
            >
              <i className="icon-bin-fill cursor-pointer align-self-center font-lg text-gray-700"></i>
            </button>
          )}
          <button
            type="button"
            className="btn btn-outline-dark py-2 font-weight-semibold font-normal px-4 mr-1"
            onClick={props.onCancel}
          >
            Cancel
          </button>
          {canCreateHoliday && !props.data?._id && (
            <button
              type="submit"
              className="btn btn-primary py-2 font-weight-semibold font-normal px-4 "
              disabled={props.isSubmitting}
              onClick={() =>
                props.onSubmit({
                  fromDate: dateFilters.startDate,
                  toDate: dateFilters.endDate,
                  _id: props.data?._id,
                  holidayTitle: watch('holidayTitle'),
                })
              }
            >
              Add
            </button>
          )}
          {canUpdateHoliday && props.data?._id && (
            <button
              type="submit"
              className="btn btn-primary py-2 font-weight-semibold font-normal px-4 "
              disabled={props.isSubmitting}
              onClick={() =>
                props.onSubmit({
                  fromDate: dateFilters.startDate,
                  toDate: dateFilters.endDate,
                  _id: props.data?._id,
                  holidayTitle: watch('holidayTitle'),
                })
              }
            >
              Update
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <form>
      <div className="p-4">
        <h4 className="mb-3 font-weight-semibold">
          {props.data && props.data._id ? 'Update ' : 'Add New '} Holiday
        </h4>
        <div className="row gutter-10">
          {renderHolidayTitle()}
          {renderDateRangeSelector()}
          <ErrorMessage>{props.error}</ErrorMessage>
          {renderButton()}
          {renderDeleteConfirm()}
        </div>
      </div>
    </form>
  );
};
export default CreateForm;
